import React, { useState, useCallback } from 'react'
import { Button, ButtonGroup, Nav, Navbar } from 'rsuite'

import PlusIcon from '@rsuite/icons/Plus'
import TableIcon from '@rsuite/icons/Table'
import CalendarIcon from '@rsuite/icons/Calendar'
import TimeIcon from '@rsuite/icons/Time'
import AssemblingShiftsTableView from '@components/assembling/shifts/AssemblingShiftsTableView'
import AssemblingShiftsCalendar from '@components/assembling/shifts/AssemblingShiftsCalendar'
import AssemblingShiftsScheduler from '@components/assembling/shifts/AssemblingShiftsScheduler'
import AssemblingShiftModal, { ShiftModalMode } from '@components/assembling/shifts/AssemblingShiftModal'
import AssemblingShiftDetailDrawer from '@components/assembling/shifts/AssemblingShiftDetailDrawer'

import { useMutateDeleteWorkShift } from '@@partner:graphql/mutation/workShift/updateWorkshift'

import './AssemblingShiftsPlanner.scss'
import { WorkShift } from '@@partner:types:app'

type ViewMode = 'table' | 'calendar'
type TabMode = 'shifts' | 'scheduler'

export default function AssemblingShiftsPlanner () {
  // Tab mode state
  const [ tabMode, setTabMode ] = useState<TabMode>('shifts')
  
  // View mode state
  const [ viewMode, setViewMode ] = useState<ViewMode>('calendar')
  
  // Modal and drawer states
  const [ isModalOpen, setIsModalOpen ] = useState(false)
  const [ isDrawerOpen, setIsDrawerOpen ] = useState(false)
  const [ modalMode, setModalMode ] = useState<ShiftModalMode>('create')
  const [ selectedShift, setSelectedShift ] = useState<WorkShift | null>(null)
  const [ modalInitialDate, setModalInitialDate ] = useState<Date | null>(null)

  const [ deleteShift ] = useMutateDeleteWorkShift()

  // Action handlers
  const handleCreateShift = useCallback((initialDate?: Date) => {
    setModalMode('create')
    setSelectedShift(null)
    setModalInitialDate(initialDate || null)
    setIsModalOpen(true)
  }, [])

  const handleEditShift = useCallback((shift: WorkShift) => {
    setModalMode('edit')
    setSelectedShift(shift)
    setModalInitialDate(null)
    setIsModalOpen(true)
  }, [])

  const handleViewShift = useCallback((shift: WorkShift) => {
    setSelectedShift(shift)
    setIsDrawerOpen(true)
  }, [])

  const handleDeleteShift = useCallback((shift: WorkShift) => {
    deleteShift({ variables: { id: shift.id } })
  }, [ deleteShift ])

  const handleDateClick = useCallback((date: Date) => {
    handleCreateShift(date)
  }, [ handleCreateShift ])

  const handleViewModeChange = useCallback((mode: ViewMode) => {
    setViewMode(mode)
  }, [])

  return (
    <div className="AssemblingShiftsPlanner">
      <Navbar  appearance='subtle'>
        <Nav 
          activeKey={tabMode} 
          onSelect={(key) => setTabMode(key as TabMode)}
          style={{ marginBottom: 20 }}
        >
          <Nav.Item eventKey="shifts" icon={<CalendarIcon />}>
            Смены сборщиков
          </Nav.Item>
          <Nav.Item eventKey="scheduler" icon={<TimeIcon />}>
            Планировщик смен
          </Nav.Item>
        </Nav>
      </Navbar>

      {tabMode === 'shifts' && (
        <>
          <div className="actions-bar">
            <div className="left-actions">
              <Button 
                appearance="primary" 
                startIcon={<PlusIcon />}
                onClick={() => handleCreateShift()}
              >
                Создать смену
              </Button>
              
              <ButtonGroup>
                <Button
                  appearance={viewMode === 'calendar' ? 'primary' : 'default'}
                  startIcon={<CalendarIcon />}
                  onClick={() => handleViewModeChange('calendar')}
                >
                  Календарь
                </Button>
                <Button
                  appearance={viewMode === 'table' ? 'primary' : 'default'}
                  startIcon={<TableIcon />}
                  onClick={() => handleViewModeChange('table')}
                >
                  Таблица
                </Button>
              </ButtonGroup>
            </div>
          </div>

          {viewMode === 'table' ? (
            <AssemblingShiftsTableView
              onEditShift={handleEditShift}
              onDeleteShift={handleDeleteShift}
              onViewShift={handleViewShift}
            />
          ) : (
            <AssemblingShiftsCalendar
              onDateClick={handleDateClick}
              onEditShift={handleEditShift}
              onViewShift={handleViewShift}
            />
          )}
        </>
      )}

      {tabMode === 'scheduler' && (
        <AssemblingShiftsScheduler />
      )}

      <AssemblingShiftModal
        mode={modalMode}
        isOpen={isModalOpen}
        shift={selectedShift}
        initialDate={modalInitialDate}
        onClose={() => {
          setIsModalOpen(false)
          setModalInitialDate(null)
        }}
        onSave={() => {
          // Each component will update its data automatically through GraphQL
        }}
        onEdit={(shift) => {
          // Switch to edit mode
          handleEditShift(shift)
        }}
      />

      <AssemblingShiftDetailDrawer
        isOpen={isDrawerOpen}
        shift={selectedShift}
        onClose={() => {
          setIsDrawerOpen(false)
          setSelectedShift(null)
        }}
        onEdit={(shift) => {
          // Close drawer and open modal for editing
          setIsDrawerOpen(false)
          handleEditShift(shift)
        }}
      />
    </div>
  )
}